/**
 * Authentication Context
 * Manages user authentication state with JWT tokens
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';

// API base URL - in production use relative /api, in dev use localhost:8002
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface User {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'viewer';
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
}

interface Tokens {
  access: string;
  refresh: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshAccessToken: () => Promise<boolean>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'r4s_omnimens_tokens';
const USER_STORAGE_KEY = 'r4s_omnimens_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<Tokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load stored auth on mount
  useEffect(() => {
    const storedTokens = localStorage.getItem(TOKEN_STORAGE_KEY);
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);

    if (storedTokens && storedUser) {
      try {
        const parsedTokens = JSON.parse(storedTokens);
        const parsedUser = JSON.parse(storedUser);
        setTokens(parsedTokens);
        setUser(parsedUser);

        // Verify token is still valid by fetching user info
        fetchCurrentUser(parsedTokens.access).catch(() => {
          // Token expired or invalid, clear auth
          clearAuth();
        });
      } catch {
        clearAuth();
      }
    }
    setIsLoading(false);
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setTokens(null);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }, []);

  const fetchCurrentUser = async (accessToken: string): Promise<User> => {
    const response = await fetch(`${API_BASE_URL}/auth/me/`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }

    return response.json();
  };

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('Login failed:', error);
        return false;
      }

      const data = await response.json();

      // Store tokens and user
      const newTokens: Tokens = data.tokens;
      const userData: User = data.user;

      setTokens(newTokens);
      setUser(userData);
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(newTokens));
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));

      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // Try to blacklist the refresh token
    if (tokens?.refresh) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh: tokens.refresh }),
        });
      } catch {
        // Ignore errors during logout
      }
    }

    clearAuth();
  }, [tokens, clearAuth]);

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    if (!tokens?.refresh) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: tokens.refresh }),
      });

      if (!response.ok) {
        clearAuth();
        return false;
      }

      const data = await response.json();
      const newTokens: Tokens = {
        access: data.access,
        refresh: data.refresh || tokens.refresh,
      };

      setTokens(newTokens);
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(newTokens));

      return true;
    } catch {
      clearAuth();
      return false;
    }
  }, [tokens, clearAuth]);

  const getAccessToken = useCallback((): string | null => {
    return tokens?.access || null;
  }, [tokens]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user && !!tokens,
        isLoading,
        isAdmin: user?.is_admin || false,
        login,
        logout,
        refreshAccessToken,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
